// Pagination component for paginated table navigation and controls
import { Select } from '@yaradigitallabs/ahua-react';
import { getClassNames } from '@/utils/helpers';
import { PaginationSkeleton } from '@/components/skeleton';

// Item renders a single page button
const Item = ({
  page,
  currentPage,
  onClick,
  isFirst = false,
  isLast = false,
}: {
  page: number;
  currentPage: number;
  onClick: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}) => (
  <button
    type="button"
    className={getClassNames(
      `text-sm cursor-pointer py-2 px-3 leading-tight border border-gray-300 focus:outline-none `,
      page === currentPage
        ? `text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-700`
        : `text-gray-500 bg-white hover:text-gray-700 hover:bg-gray-100`,
      isFirst ? 'rounded-l-lg' : '',
      isLast ? 'rounded-r-lg' : ''
    )}
    onClick={onClick}
    onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    }}
    aria-label={`Go to page ${page}`}
    aria-current={page === currentPage ? 'page' : undefined}
  >
    {page}
  </button>
);

// Previous renders the previous page button
const Previous = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    className="text-sm cursor-pointer py-2 px-3 ml-0 leading-tight text-gray-500 bg-white rounded-l-lg border border-gray-300 hover:bg-gray-100 hover:text-gray-700 focus:outline-none"
    onClick={onClick}
    onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    }}
    aria-label="Go to previous page"
  >
    Previous
  </button>
);

// Next renders the next page button
const Next = ({ onClick }: { onClick: () => void }) => (
  <button
    type="button"
    className="text-sm cursor-pointer py-2 px-3 leading-tight text-gray-500 bg-white rounded-r-lg border border-gray-300 hover:bg-gray-100 hover:text-gray-700 focus:outline-none"
    onClick={onClick}
    onKeyDown={e => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    }}
    aria-label="Go to next page"
  >
    Next
  </button>
);

// Ellipsis renders the ... between page numbers
const Ellipsis = () => (
  <div className="text-sm py-2 px-3 leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700">
    ...
  </div>
);

// Props for Pagination component
interface PaginationProps {
  loading: boolean; // Is data loading
  dataCount: number; // Total number of items
  limitOptions?:
    | { text: string; value: string }[]
    | { text: string; value: number }[];
  paginatedData: { size: number; page: number }; // Current page and size
  setPaginatedData: ({ size, page }: { size: number; page: number }) => void; // Setter for pagination state
  className?: string;
}

// Pagination renders page controls, limit selector, and navigation
const Pagination = ({
  loading,
  dataCount,
  limitOptions,
  className,
  paginatedData,
  setPaginatedData,
}: PaginationProps) => {
  const totalPages = Math.ceil(dataCount / paginatedData.size);
  const currentPage = paginatedData.page;
  const pageOptions = [-1, 0, 1]
    .map(page => currentPage + page)
    .filter(page => page > 0 && page <= totalPages);
  const startIdx = Math.min(
    (paginatedData.page - 1) * paginatedData.size + 1,
    dataCount
  );
  const endIdx = Math.min(paginatedData.page * paginatedData.size, dataCount);

  // Show skeleton while loading
  if (loading) {
    return <PaginationSkeleton />;
  }

  // Handler for changing page size
  const onLimitChange = (value: string) => {
    setPaginatedData({ size: parseInt(value, 10), page: 1 });
  };

  return (
    <>
      {dataCount > 0 && (
        <div
          className={`${className} flex gap-4 justify-between items-end p-4`}
        >
          {/* Page size selector and result count */}
          <div className="flex flex-wrap items-center justify-start text-sm gap-2">
            <p>{'Show '}</p>
            {!!limitOptions?.length && (
              <Select
                ariaLabel="Select number of items per page"
                size="xs"
                css={{
                  width: '100px',
                }}
                variant="default"
                value={paginatedData.size.toString()}
                items={limitOptions}
                onChange={value => onLimitChange(value)}
              />
            )}
            <p>
              {startIdx} - {endIdx} of {dataCount} results
            </p>
          </div>

          {/* Pagination navigation controls */}
          <nav className="flex" aria-label="Pagination Navigation">
            {startIdx !== 1 && (
              <Previous
                onClick={() =>
                  setPaginatedData({
                    ...paginatedData,
                    page: Math.max(paginatedData.page - 1, 1),
                  })
                }
              />
            )}
            {/* First page and ellipsis if needed */}
            {!pageOptions.includes(1) && (
              <>
                <Item
                  page={1}
                  currentPage={currentPage}
                  onClick={() =>
                    setPaginatedData({ ...paginatedData, page: 1 })
                  }
                />
                <Ellipsis />
              </>
            )}
            {/* Main page options */}
            {pageOptions.map(page => (
              <Item
                isFirst={page === 1 && startIdx === 1}
                isLast={page === totalPages && currentPage === totalPages}
                key={page}
                page={page}
                currentPage={currentPage}
                onClick={() =>
                  setPaginatedData({ ...paginatedData, page: page })
                }
              />
            ))}
            {/* Last page and ellipsis if needed */}
            {!pageOptions.includes(totalPages) && (
              <>
                <Ellipsis />
                <Item
                  isLast={endIdx === dataCount}
                  page={totalPages}
                  currentPage={currentPage}
                  onClick={() =>
                    setPaginatedData({
                      ...paginatedData,
                      page: totalPages,
                    })
                  }
                />
              </>
            )}
            {/* Next page button if not at end */}
            {endIdx !== dataCount && (
              <Next
                onClick={() =>
                  setPaginatedData({
                    ...paginatedData,
                    page: Math.min(paginatedData.page + 1, totalPages),
                  })
                }
              />
            )}
          </nav>
        </div>
      )}
    </>
  );
};

export default Pagination;
